<template>
  <div v-if="normalizedFiles.length"
       class="training-media-preview">
    <div class="training-media-preview__grid">
      <div v-for="(file, index) in normalizedFiles"
           :key="fileKey(file, index)"
           class="training-media-preview__item"
           @click="openViewer(index)">
        <img v-if="file.type === 'img'"
             :src="file.url"
             class="training-media-preview__thumb"
             alt="">
        <div v-else-if="file.type === 'video'"
             class="training-media-preview__video">
          <div class="training-media-preview__video-placeholder">
            <i class="el-icon-video-camera" />
          </div>
          <div class="training-media-preview__play-overlay">
            <i class="el-icon-video-play" />
          </div>
        </div>
      </div>
    </div>

    <el-dialog :visible.sync="viewer.visible"
               width="100%"
               top="0"
               append-to-body
               custom-class="training-media-preview__viewer-dialog"
               :close-on-click-modal="false"
               :close-on-press-escape="true"
               :show-close="false"
               @closed="onViewerClosed"
               @opened="onViewerOpened">
      <div v-if="viewer.visible"
           class="training-media-preview__viewer">
        <button type="button"
                class="training-media-preview__viewer-close el-dialog__headerbtn"
                @click="closeViewer">
          <i class="el-dialog__close el-icon el-icon-close" />
        </button>

        <div class="training-media-preview__viewer-counter">
          <span class="training-media-preview__viewer-counter-current">{{ viewerIndex + 1 }}</span>
          <span class="training-media-preview__viewer-counter-total">/{{ normalizedFiles.length }}</span>
        </div>

        <div class="training-media-preview__viewer-body">
          <img v-if="activeFile && activeFile.type === 'img'"
               :key="'img-' + viewerIndex"
               :src="activeFile.url"
               class="training-media-preview__viewer-image"
               alt="">
          <video v-else-if="activeFile && activeFile.type === 'video'"
                 :key="'video-' + viewerIndex"
                 ref="videoPlayer"
                 :src="activeFile.url"
                 controls
                 autoplay
                 playsinline
                 class="training-media-preview__viewer-video" />
        </div>

        <span v-if="canGoPrev"
              class="el-image-viewer__btn el-image-viewer__prev training-media-preview__viewer-nav"
              @click="viewPrev">
          <i class="el-icon-arrow-left" />
        </span>
        <span v-if="canGoNext"
              class="el-image-viewer__btn el-image-viewer__next training-media-preview__viewer-nav"
              @click="viewNext">
          <i class="el-icon-arrow-right" />
        </span>

        <div class="training-media-preview__viewer-navbar">
          <div v-for="(file, index) in normalizedFiles"
               :key="'nav-' + fileKey(file, index)"
               :class="['training-media-preview__viewer-nav-item', { 'is-active': viewerIndex === index }]"
               @click="viewIndex(index)">
            <img v-if="file.type === 'img'"
                 :src="file.url"
                 alt="">
            <div v-else
                 class="training-media-preview__viewer-nav-video">
              <i class="el-icon-video-camera" />
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'TrainingMediaPreview',
  props: {
    files: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      viewer: {
        visible: false,
        index: 0,
      },
    }
  },
  computed: {
    normalizedFiles () {
      return (this.files || []).filter(file => file && file.url && (file.type === 'img' || file.type === 'video'))
    },
    viewerIndex () {
      return this.viewer.index
    },
    activeFile () {
      return this.normalizedFiles[this.viewerIndex] || null
    },
    canGoPrev () {
      return this.viewerIndex > 0
    },
    canGoNext () {
      return this.viewerIndex < this.normalizedFiles.length - 1
    },
  },
  mounted () {
    document.addEventListener('keyup', this.onKeyup)
  },
  beforeDestroy () {
    document.removeEventListener('keyup', this.onKeyup)
  },
  methods: {
    fileKey (file, index) {
      return file.uid || file.url || index
    },
    openViewer (index) {
      if (index < 0 || index >= this.normalizedFiles.length) {
        return
      }
      this.viewer.index = index
      this.viewer.visible = true
    },
    closeViewer () {
      this.viewer.visible = false
    },
    onViewerOpened () {
      this.pauseVideo()
    },
    onViewerClosed () {
      this.pauseVideo()
      this.viewer.index = 0
    },
    viewIndex (index) {
      if (index === this.viewerIndex || index < 0 || index >= this.normalizedFiles.length) {
        return
      }
      this.pauseVideo()
      this.viewer.index = index
    },
    viewPrev () {
      if (!this.canGoPrev) {
        return
      }
      this.viewIndex(this.viewerIndex - 1)
    },
    viewNext () {
      if (!this.canGoNext) {
        return
      }
      this.viewIndex(this.viewerIndex + 1)
    },
    pauseVideo () {
      const player = this.$refs.videoPlayer
      if (player && typeof player.pause === 'function') {
        player.pause()
      }
    },
    onKeyup (event) {
      if (!this.viewer.visible) {
        return
      }
      if (event.keyCode === 37) {
        this.viewPrev()
      } else if (event.keyCode === 39) {
        this.viewNext()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.training-media-preview {
  margin-top: 10px;

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 10px;
    max-width: 640px;
  }

  &__item {
    position: relative;
    aspect-ratio: 1;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    background: #f2f2f2;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &__thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__video {
    position: relative;
    width: 100%;
    height: 100%;
    background: #eef2f7;
  }

  &__video-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #c0c4cc;
    font-size: 28px;
  }

  &__play-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.12);

    i {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: rgba(64, 158, 255, 0.88);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 24px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    }
  }

  &__viewer {
    position: relative;
    width: 100%;
    height: 100vh;
    background: rgba(0, 0, 0, 0.92);
    overflow: hidden;
  }

  &__viewer-close {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2003;
    background-color: rgba(255, 255, 255, 0.1);
    padding: 30px;
    border: 0;
    border-radius: 0 0 0 100%;
    cursor: pointer;

    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
    }

    i {
      color: #fff;
      font-size: 18px;
    }
  }

  &__viewer-counter {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 2002;
    color: #909399;
    text-shadow: 0 0 2px #000;

    &-current {
      font-size: 22px;
      color: #fff;
    }

    &-total {
      font-size: 14px;
    }
  }

  &__viewer-body {
    width: 100%;
    height: calc(100% - 72px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 72px 0;
    box-sizing: border-box;
  }

  &__viewer-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  &__viewer-video {
    max-width: 100%;
    max-height: 100%;
    background: #000;
  }

  &__viewer-nav {
    z-index: 2002;
  }

  &__viewer-navbar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2002;
    display: flex;
    justify-content: center;
    gap: 8px;
    padding: 12px 16px 16px;
    overflow-x: auto;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.55), transparent);
  }

  &__viewer-nav-item {
    flex: 0 0 auto;
    width: 56px;
    height: 56px;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    opacity: 0.65;
    border: 2px solid transparent;
    background: rgba(255, 255, 255, 0.08);

    &.is-active {
      opacity: 1;
      border-color: #409eff;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  &__viewer-nav-video {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 22px;
  }
}
</style>

<style lang="scss">
.training-media-preview__viewer-dialog {
  margin: 0 !important;
  background: transparent;
  box-shadow: none;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/training/components/TrainingMediaPreview.vue